import { Modal } from 'antd';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CookiebotDOM, CookiebotEvents } from './shared/components/cookiebot-provider';

export interface AnalyticsProps {
  active?: boolean;
}

/**
 * La propiedad de GA4 de la aplicación. Una sola fuente: la usan el fragmento de gtag.js de abajo y
 * `safeGtag`. Estaba escrita tres veces a mano, y esa deriva es muda: si una migración de propiedad
 * actualiza el fragmento y se salta la constante, `send_to` nombra un destino que no existe y los
 * eventos dejan de enviarse sin error en consola ni petición fallida.
 */
export const GA4_MEASUREMENT_ID = 'G-YMP95L0FLW';

/**
 * El píxel de Meta. Mismo motivo que `GA4_MEASUREMENT_ID`: lo consumen el fragmento de abajo y los
 * `safeFbq('init', ...)` que añaden emparejamiento avanzado al iniciar sesión.
 */
export const META_PIXEL_ID = '1710350250324765';

// El contenedor de GTM no está aquí: lo carga el arranque del `<head>`, que corre antes de que React
// exista y no puede importar una constante de TypeScript. Si cambia, hay TRES copias que actualizar:
// el `ClientApp/index.html` de este repo, y en j2l-identity el suyo y `Views/Shared/_Layout.cshtml`.
const LINKEDIN_PARTNER_ID = '7941057';
const HUBSPOT_PORTAL_ID = '144070489';
const CONTENTSQUARE_SRC = 'https://t.contentsquare.net/uxa/a56c4d3171602.js';

/**
 * `resolved` es la distinción que no se puede perder: separa «Cookiebot todavía no ha contestado» de
 * «ha contestado y lo ha denegado todo». Sin ella las dos situaciones son el mismo par de booleanos a
 * `false`, y entonces el primer render, que siempre ocurre antes de que Cookiebot resuelva, emite un
 * `consent update` denegándolo todo: consume la ventana de `wait_for_update` y le da la vuelta al
 * consentimiento que Cookiebot ya había concedido.
 *
 * `preferences` no decide qué se inyecta, porque ninguna de estas herramientas guarda preferencias
 * del visitante. Está porque Consent Mode sí tiene dos señales que dependen de ella y que el bloque
 * de arranque deja denegadas, así que alguien tiene que levantarlas.
 */
interface ConsentState {
  resolved: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
}

const UNRESOLVED: ConsentState = { resolved: false, preferences: false, statistics: false, marketing: false };

/**
 * Puede no existir todavía: el script de Cookiebot lo inyecta la shell HTML y llega de forma
 * asíncrona, así que entre el montaje de React y su carga `window.Cookiebot` es `undefined`. Y
 * `window` tampoco se da por hecho, porque de aquí cuelgan `safeFbq` y `safeLintrk`, que sí se
 * guardan de un contexto sin DOM.
 */
const getCookiebot = (): CookiebotDOM | undefined => (typeof window === 'undefined' ? undefined : (window.Cookiebot as CookiebotDOM | undefined));

const readConsent = (): ConsentState => {
  const cookiebot = getCookiebot();
  if (cookiebot?.hasResponse !== true) return UNRESOLVED;

  return {
    resolved: true,
    preferences: cookiebot.consent?.preferences === true,
    statistics: cookiebot.consent?.statistics === true,
    marketing: cookiebot.consent?.marketing === true
  };
};

const sameConsent = (a: ConsentState, b: ConsentState): boolean =>
  a.resolved === b.resolved && a.preferences === b.preferences && a.statistics === b.statistics && a.marketing === b.marketing;

/**
 * Sigue el consentimiento vivo, no solo el del primer render.
 *
 * `CookiebotOnConsentReady` basta por sí solo: lo dispara tanto la carga con consentimiento ya
 * almacenado en la cookie como el momento en que el visitante responde al banner o lo renueva desde la
 * política de cookies. `CookiebotOnAccept` y `CookiebotOnDecline` están como redundancia, para
 * reaccionar al primero que llegue: aquí el consentimiento decide además qué etiquetas se inyectan y
 * si hay que recargar, así que llegar tarde cuesta más que suscribirse de más.
 *
 * El arranque del `<head>` y `Views/Shared/_Layout.cshtml` en j2l-identity escuchan solo
 * `CookiebotOnConsentReady`, y es correcto por lo mismo: allí no hay nada que inyectar.
 *
 * No sirve el `useCookiebot` del provider: guarda el propio objeto `window.Cookiebot`, que es
 * siempre la misma referencia, así que React descarta el re-render y el cambio de consentimiento no
 * se ve. Aquí el estado son booleanos, que sí cambian de valor.
 */
const useCookiebotConsent = (active: boolean): ConsentState => {
  const [consent, setConsent] = useState<ConsentState>(UNRESOLVED);

  const sync = useCallback(() => {
    const next = readConsent();
    setConsent((prev) => (sameConsent(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    if (!active) return;

    // Por si Cookiebot ya había resuelto el consentimiento antes de que este efecto se suscribiera.
    sync();

    const events = [CookiebotEvents.CookiebotOnConsentReady, CookiebotEvents.CookiebotOnAccept, CookiebotEvents.CookiebotOnDecline];
    events.forEach((e) => window.addEventListener(e, sync));
    return () => events.forEach((e) => window.removeEventListener(e, sync));
  }, [active, sync]);

  return consent;
};

/**
 * Inyecta una sola vez: el `id` hace de guarda, así que un re-render no duplica la etiqueta.
 *
 * `data-cookieconsent="ignore"` no es una laguna. Estas etiquetas ya están detrás del consentimiento
 * por el efecto que las inyecta, y sin la marca pasarían además por el clasificador del bloqueo
 * automático, que es una segunda puerta con criterio propio: puede bloquear algo que nosotros sí
 * hemos concedido, y puede reejecutar un script inline al conceder el consentimiento, que en el
 * fragmento de Meta significa un `PageView` duplicado. Una sola puerta, y es esta.
 */
const injectScript = (id: string, options: { src?: string; text?: string; parent?: HTMLElement }): void => {
  if (document.getElementById(id) != null) return;

  const script = document.createElement('script');
  script.id = id;
  script.setAttribute('data-cookieconsent', 'ignore');
  if (options.text != null) script.text = options.text;
  // `src` al final: con el bloqueo automático activo, Cookiebot observa la asignación de `src` para
  // decidir, y la exención tiene que estar puesta ya cuando eso ocurre.
  if (options.src != null) {
    script.async = true;
    script.src = options.src;
  }

  (options.parent ?? document.head).appendChild(script);
};

/**
 * Llamadas a `fbq` o `lintrk` hechas CON consentimiento de marketing pero antes de que el efecto haya
 * inyectado la etiqueta. Es una carrera real: `onSigninCallback` en `index.tsx`,
 * `getPersonProfileStatus` en `default-user-page-view.tsx` y los dos `useMount` de onboarding
 * disparan en cuanto resuelven sus promesas, y pueden adelantar al evento de Cookiebot.
 *
 * Lo que NO entra aquí es la actividad previa al consentimiento: si al llamar no hay consentimiento
 * de marketing resuelto, la llamada se descarta y no se guarda. La cola arregla el desfase técnico,
 * no reintroduce lo que este cambio viene a cortar.
 *
 * El tope existe porque hay un camino sin salida: si el fragmento nunca llega a ejecutarse (una
 * extensión que lo bloquee, por ejemplo), el efecto no vuelve a correr y nadie vacía la cola.
 */
const MAX_PENDING_CALLS = 50;
const pendingFbqCalls: unknown[][] = [];
const pendingLintrkCalls: [string, Record<string, unknown> | undefined][] = [];

const enqueue = <T,>(queue: T[], call: T): void => {
  // Se rechaza lo nuevo en vez de descartar lo viejo a propósito: lo primero que entra suele ser el
  // `fbq('init', ..., userData)` del emparejamiento avanzado, y los `track` que vienen detrás dependen
  // de él. Cincuenta llamadas encoladas ya es una situación que no deberia darse.
  if (queue.length >= MAX_PENDING_CALLS) return;
  queue.push(call);
};

const flushPendingCalls = (): void => {
  if (typeof fbq !== 'undefined') {
    while (pendingFbqCalls.length > 0) {
      const args = pendingFbqCalls.shift();
      if (args != null) (fbq as any)(...args);
    }
  }

  if (typeof window.lintrk === 'function') {
    while (pendingLintrkCalls.length > 0) {
      const call = pendingLintrkCalls.shift();
      if (call != null) window.lintrk(call[0], call[1]);
    }
  }
};

/**
 * Las cuatro piezas que se cargan, cada una con su puerta.
 *
 * **La puerta de una herramienta es la conjunción de TODAS las categorías bajo las que están
 * declaradas sus cookies**, no la de la categoría que mejor la describa. El motivo es que el
 * consentimiento lo da el visitante contra las categorías que le enseña el banner, y ese banner lista
 * las cookies por la clasificación del panel de Cookiebot. Si la declaración dice que `__hstc` es de
 * estadística y alguien rechaza estadística, esa cookie no puede escribirse, aunque quien la ponga
 * sea una herramienta que uno considere de marketing.
 *
 * De ahí las dos puertas dobles: las cookies de HubSpot están declaradas entre estadística
 * (`__hstc`, `hubspotutk`, `__hssc`, `__hssrc`) y marketing (`__ptq.gif`), y las de LinkedIn entre
 * marketing (`collect/`) y preferencias (`lidc`). Las declaradas como necesarias no restringen nada,
 * porque no requieren consentimiento.
 *
 * Si alguna vez se reclasifica una cookie en el panel, esta tabla es lo que hay que mover con ella, y
 * la tabla de `cookies-policy-page.tsx` detrás. Las tres dicen lo mismo a propósito.
 */
type Tool = 'analytics' | 'meta' | 'linkedin' | 'hubspot';

const TOOLS: Tool[] = ['analytics', 'meta', 'linkedin', 'hubspot'];

const gateFor = (consent: { preferences: boolean; statistics: boolean; marketing: boolean }): Record<Tool, boolean> => ({
  analytics: consent.statistics,
  meta: consent.marketing,
  linkedin: consent.marketing && consent.preferences,
  hubspot: consent.statistics && consent.marketing
});

/**
 * Lo que se puede apagar en caliente al retirar el consentimiento, que no es todo.
 *
 * Meta acepta `revoke` y `grant`, así que su apagado es reversible. HubSpot y Contentsquare solo
 * tienen apagado: cortan el envío, pero su script sigue cargado y su sesión abierta, y volver a
 * conceder no los reactiva. Por eso esto no sustituye a la oferta de recarga, que es la única
 * respuesta completa; lo que hace es parar la medición en el acto sin esperar a que el visitante
 * acepte recargar. GA4 no está aquí porque de él se encarga el `consent update` de Consent Mode.
 *
 * Los tres comandos están comprobados contra el código que sirven hoy los proveedores, no contra su
 * documentación: `optout` sale de la lista de comandos de `t.contentsquare.net/uxa/...`, y
 * `doNotTrack` y `revokeCookieConsent` del bundle de `js-eu1.hs-analytics.net`.
 */
const stopVendorTracking = (stale: Record<Tool, boolean>): void => {
  if (stale.meta && typeof fbq !== 'undefined') (fbq as any)('consent', 'revoke');

  if (stale.hubspot) {
    // `revokeCookieConsent` además borra sus cookies.
    window._hsq = window._hsq ?? [];
    window._hsq.push(['doNotTrack'], ['revokeCookieConsent']);
  }

  if (stale.analytics) {
    window._uxa = window._uxa ?? [];
    window._uxa.push(['optout']);
  }
};

/**
 * La otra mitad de `stopVendorTracking`, y solo para Meta porque es el único que vuelve.
 */
const resumeMetaTracking = (): void => {
  if (typeof fbq !== 'undefined') (fbq as any)('consent', 'grant');
};

/**
 * Carga las etiquetas de analítica y de marketing, cada una detrás de su consentimiento.
 *
 * Lo que NO está aquí, y es deliberado:
 *
 * - **GTM, el bloque de Consent Mode y el `update` que lo sigue** viven en `ClientApp/index.html`.
 *   Tienen que ejecutarse durante el parseo del `<head>`, antes que Cookiebot y antes que cualquier
 *   otra etiqueta; desde React es imposible, porque para cuando React monta el `<head>` ya se parseó.
 *   Ese era el fallo de Jobs-2-Live/j2l-website#1816.
 *
 * - **Los `<noscript>` de respaldo** de GTM, Meta y LinkedIn. Sin JavaScript Cookiebot no puede
 *   ejecutarse, así que no hay banner, no hay consentimiento posible y esos tres píxeles disparaban
 *   igual. Un respaldo que solo actúa cuando el consentimiento es inalcanzable es exactamente lo que
 *   no puede quedarse.
 *
 * El bloqueo automático de Cookiebot sigue actuando por encima de todo esto, y es el que cubre lo
 * que cargue el contenedor de GTM, que no pasa por aquí.
 */
const Analytics: FC<PropsWithChildren<AnalyticsProps>> = ({ active = false, children }) => {
  const { t } = useTranslation();
  const [modal, modalContextHolder] = Modal.useModal();
  const { resolved, preferences, statistics, marketing } = useCookiebotConsent(active);

  // Qué se ha llegado a inyectar, por herramienta. Es lo que decide si hay algo que apagar, y no la
  // transición del consentimiento: ver el bloque de documentación del efecto que ofrece recargar.
  const injected = useRef<Record<Tool, boolean>>({ analytics: false, meta: false, linkedin: false, hubspot: false });
  const everResolved = useRef(false);
  const openConfirm = useRef<ReturnType<typeof modal.confirm> | null>(null);

  // El par de categorías obsoletas sobre el que ya se actuó. Sin esto, el efecto es un predicado de
  // estado y no de transición, así que cada reejecución por identidad de `modal` o de `t` volvería a
  // abrir el diálogo después de que el visitante lo hubiera cerrado con «Ahora no».
  const handled = useRef<string | null>(null);

  /**
   * Respaldo del `update` de Consent Mode. El que cuenta lo manda el arranque del `<head>`, que se
   * suscribe a `CookiebotOnConsentReady` antes de cargar Cookiebot y por tanto llega dentro de la
   * ventana de `wait_for_update`. Este no puede llegar a tiempo a la primera medición (no corre hasta
   * que el bundle ha parseado), así que su papel es cubrir los cambios de consentimiento posteriores
   * y ser idempotente con el otro.
   */
  useEffect(() => {
    if (!active) return;

    if (resolved) {
      everResolved.current = true;
      window.gtag?.('consent', 'update', {
        analytics_storage: statistics ? 'granted' : 'denied',
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied',
        // El bloque de arranque las deja denegadas y nadie más las revisa. Sin esto, un visitante que
        // acepta preferencias las arrastra denegadas toda la sesión.
        functionality_storage: preferences ? 'granted' : 'denied',
        personalization_storage: preferences ? 'granted' : 'denied'
      });
      return;
    }

    // Volver a «sin respuesta» después de haber respondido es lo que hace `Cookiebot.withdraw()`.
    // Hoy no lo llama nadie, pero está a un ajuste del panel de Cookiebot de estarlo, y el fallo
    // sería mudo en la dirección mala: sin esto, Consent Mode se quedaría en el último `granted`
    // durante el resto de la vida de la página. No se ofrece recargar aquí, porque en ese flujo
    // Cookiebot vuelve a enseñar el banner y encadenar un diálogo encima sobra.
    if (!everResolved.current) return;
    window.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied'
    });
  }, [active, resolved, preferences, statistics, marketing]);

  /**
   * Retirar el consentimiento tiene que apagar lo que ya está cargado. Dos pasos, en este orden:
   *
   * 1. **Apagar lo que tiene interruptor.** Meta, HubSpot y Contentsquare publican su propia
   *    retirada y se llama aquí, en `stopVendorTracking`. Para la medición en el acto, sin depender
   *    de que el visitante acepte nada.
   * 2. **Ofrecer la recarga** para el resto. Una etiqueta de terceros no se descarga: su script
   *    sigue en la página con su sesión abierta hasta que la página muera, y de HubSpot y
   *    Contentsquare ni siquiera vuelve el envío al reconceder. Recargar es la única respuesta
   *    completa.
   *
   * **La decisión se toma sobre lo que se llegó a INYECTAR, no sobre cómo cambió el consentimiento.**
   * Con la transición, retirar estadística y conceder marketing después cerraba el aviso de la
   * primera retirada -- el consentimiento «había cambiado», aunque no hacia lo retirado -- y dejaba
   * GA4, Contentsquare y HubSpot corriendo sin que nadie volviera a avisar. `injected` convierte el
   * predicado en «hay algo cargado cuya categoría ya no está concedida», que es exactamente lo que el
   * aviso dice, y no depende del camino por el que se llegó.
   *
   * `resolved` sí hace falta: `Cookiebot.renew()`, que es lo que hace el botón «Actualizar
   * consentimiento» de la política de cookies, devuelve el estado a «sin respuesta» y vuelve a
   * enseñar el banner. Sin esa guarda, esa ventana se leería como retirada y ofrecería recargar con
   * el banner abierto, dejando ese botón convertido en un recargador que no hace nada.
   *
   * Se pregunta antes de recargar porque la política de cookies se abre como cajón desde el pie de
   * las landings, o sea en la misma página que el formulario de contacto, y recargar sin avisar se
   * lleva por delante lo que el visitante estuviera escribiendo. El foco va al botón de no recargar,
   * que es el que no destruye nada. Si dice que no, lo que no tiene interruptor sigue cargado hasta
   * que recargue o se vaya: es su decisión.
   */
  useEffect(() => {
    if (!active || !resolved) return;

    // Una herramienta está obsoleta cuando se cargó y su puerta ya no se cumple.
    const gate = gateFor({ preferences, statistics, marketing });
    const stale = TOOLS.reduce((acc, tool) => ({ ...acc, [tool]: injected.current[tool] && !gate[tool] }), {} as Record<Tool, boolean>);

    const key = TOOLS.map((tool) => stale[tool]).join('|');
    if (key === handled.current) return;
    handled.current = key;

    if (!TOOLS.some((tool) => stale[tool])) {
      // Todo lo cargado vuelve a tener su consentimiento, así que un aviso abierto ya no describe
      // nada. Y lo que se apagó y sabe volver, vuelve.
      openConfirm.current?.destroy();
      if (injected.current.meta) resumeMetaTracking();
      return;
    }

    if (stale.meta) {
      // Lo que quedara encolado ya no se va a enviar nunca, y lleva el emparejamiento avanzado del
      // píxel. De aquí no sale de todas formas, pero no tiene sentido seguir guardándolo.
      pendingFbqCalls.length = 0;
    }

    if (stale.linkedin) pendingLintrkCalls.length = 0;

    stopVendorTracking(stale);

    // La instancia se guarda antes de asignarla y `afterClose` comprueba identidad: `destroy()` solo
    // arranca la animación de cierre, y para cuando el `afterClose` del diálogo viejo corre, la ref ya
    // apunta al nuevo. Sin la comprobación, el viejo al cerrarse dejaría la ref a null con el nuevo en
    // pantalla.
    openConfirm.current?.destroy();
    const instance = modal.confirm({
      title: t('Has cambiado tus preferencias de cookies'),
      content: t(
        'Hemos dejado de enviar lo que se podía parar sin recargar. El resto de herramientas ya cargadas siguen activas hasta que la página se recargue. Si recargas ahora perderás lo que hayas escrito sin guardar.'
      ),
      okText: t('Recargar ahora'),
      cancelText: t('Ahora no'),
      focusable: { autoFocusButton: 'cancel' },
      // Ningun boton de esta app es rectangular: sin esto, estos dos serian los unicos.
      okButtonProps: { shape: 'round' },
      cancelButtonProps: { shape: 'round' },
      // La promesa no se resuelve nunca a propósito: deja el botón en estado de carga mientras el
      // navegador tira la página, que si no invita a un segundo clic.
      onOk: () => {
        window.location.reload();
        return new Promise<void>(() => undefined);
      },
      afterClose: () => {
        if (openConfirm.current === instance) openConfirm.current = null;
      }
    });

    openConfirm.current = instance;
  }, [active, resolved, preferences, statistics, marketing, modal, t]);

  useEffect(() => () => openConfirm.current?.destroy(), []);

  useEffect(() => {
    if (!active || !statistics) return;

    // gtag.js aparte del contenedor, que es como estaba. El `send_to` de `safeGtag` depende de esta
    // propiedad concreta: lee el comentario de `safeGtag` antes de tocarlo.
    injectScript('j2l-ga4', { src: `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}` });
    injectScript('j2l-ga4-config', {
      text: `
        window.dataLayer = window.dataLayer || [];
        window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', '${GA4_MEASUREMENT_ID}');
      `
    });

    injectScript('j2l-contentsquare', { src: CONTENTSQUARE_SRC });

    injected.current.analytics = true;
  }, [active, statistics]);

  useEffect(() => {
    if (!active || !marketing) return;

    injectScript('j2l-meta-pixel', {
      text: `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${META_PIXEL_ID}');
        fbq('track', 'PageView');
      `
    });

    injected.current.meta = true;

    // El fragmento es inline, así que ya ha corrido al volver de `injectScript` y su cola
    // (`fbq.queue`) existe. Lo que quedara encolado aquí se manda ahora.
    flushPendingCalls();
  }, [active, marketing]);

  useEffect(() => {
    // La etiqueta de LinkedIn escribe `lidc`, que la declaración clasifica como cookie de
    // preferencias, además de `collect/`, que es de marketing. Por eso espera a las dos.
    if (!active || !marketing || !preferences) return;

    injectScript('j2l-linkedin-insight', {
      text: `
        _linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);
        (function(l) {
          if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
          window.lintrk.q=[]}
          var s = document.getElementsByTagName("script")[0];
          var b = document.createElement("script");
          b.type = "text/javascript";b.async = true;
          b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
          s.parentNode.insertBefore(b, s);})(window.lintrk);
      `
    });

    injected.current.linkedin = true;
    flushPendingCalls();
  }, [active, marketing, preferences]);

  useEffect(() => {
    // HubSpot escribe cuatro cookies declaradas como de estadística (`__hstc`, `hubspotutk`,
    // `__hssc`, `__hssrc`) y el píxel `__ptq.gif`, declarado como publicitario. Por eso espera a las
    // dos categorías: con una sola, la otra mitad se escribiría sin su consentimiento.
    if (!active || !statistics || !marketing) return;

    // El id es el que documenta HubSpot: su propio cargador y sus integraciones identifican la
    // etiqueta por él.
    injectScript('hs-script-loader', { src: `//js-eu1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`, parent: document.body });

    injected.current.hubspot = true;
  }, [active, statistics, marketing]);

  return (
    <>
      {modalContextHolder}
      {children}
    </>
  );
};

export default Analytics;

/**
 * Consentimiento de estadistica ya resuelto. Lo exporta para los emisores que no viven en este fichero
 * y que, sin esto, empujarian al `dataLayer` sin mirar nada: ver `common/gtm/services.ts`.
 */
export const hasStatisticsConsent = (): boolean => readConsent().statistics;

/**
 * **El consentimiento se relee en CADA llamada, no solo al encolar.** Una vez inyectado, el píxel deja
 * `fbq` definido el resto de la vida de la página: comprobar solo en el camino de la cola dejaba pasar
 * todo lo posterior a una retirada, y lo primero que pasa por aquí es
 * `safeFbq('init', ..., userData)` con el correo hasheado del visitante dentro. Retirar el
 * consentimiento y decir «Ahora no» seguía mandándolo.
 *
 * Con consentimiento concedido y el píxel aún sin inyectar, la llamada se encola y se manda en cuanto
 * el efecto lo inyecta. Sin consentimiento no se encola nada: se descarta.
 */
export const safeFbq = (...args: any[]): void => {
  if (!readConsent().marketing) return;

  if (typeof fbq !== 'undefined') {
    (fbq as any)(...args);
    return;
  }

  enqueue(pendingFbqCalls, args);
};

/**
 * Mismo contrato que `safeFbq`, para LinkedIn. **Llámala siempre en vez de `window.lintrk`**: desde
 * que la etiqueta espera al consentimiento, un `window.lintrk(...)` directo revienta cuando aún no
 * está cargada, y envuelto en un `try/catch` vacío la conversión se pierde sin dejar rastro.
 */
export const safeLintrk = (action: string, conversionData?: Record<string, unknown>): void => {
  // La misma puerta que la etiqueta: ver `gateFor`.
  const consent = readConsent();
  if (!consent.marketing || !consent.preferences) return;

  if (typeof window !== 'undefined' && typeof window.lintrk === 'function') {
    window.lintrk(action, conversionData);
    return;
  }

  enqueue(pendingLintrkCalls, [action, conversionData]);
};

/**
 * Envía un evento a GA4 por `gtag`, no por `dataLayer.push`. El push al dataLayer no llega a GA4 por
 * sí solo: haría falta un activador y una etiqueta en el contenedor GTM.
 *
 * EL `send_to` NO ES OPCIONAL AQUÍ, aunque la documentación de Google lo presente como opcional.
 *
 * Medido en producción el 2026-09-07 en la pestaña de red, y esto es el hecho: `gtag('event', 'x')`
 * no genera NINGUNA petición a `/g/collect`, y el mismo evento con `send_to` responde 204. El
 * `page_view` y el `form_start` que sí se ven no salen de aquí, los genera la medición mejorada que
 * configura el contenedor GTM.
 *
 * La explicación más probable, que ya es inferencia y no medición: la propiedad está cargada y sabe
 * enviar (lo demuestra el 204), pero no está en el conjunto de destinos por defecto al que enruta un
 * `event` sin destinatario, porque quien la configuró fue la etiqueta del contenedor y no nuestro
 * `gtag('config', ...)`. `send_to` la nombra y se salta ese conjunto. Si algún día cambia el
 * contenedor, vuelve a medirlo antes de quitar el `send_to`; no lo quites por leer la documentación.
 *
 * TRAMPA PARA EL FUTURO: si alguien añade en GTM una etiqueta de evento de GA4 disparada por
 * `generate_lead` contra esta misma propiedad, el evento se contará DOS veces, porque esa etiqueta y
 * esta llamada son dos emisores independientes. Hoy no existe: si existiera, el push al dataLayer
 * que ya ocurría en 6.0.20 habría generado una petición, y no la generaba.
 *
 * Fuera de producción `window.gtag` no existe, porque el arranque del `<head>` se salta entero y no
 * hace nada. En producción sin consentimiento de estadística sí existe, lo define el bloque de
 * Consent Mode: el evento se queda en el dataLayer y lo recoge gtag.js cuando llega a cargar, o sea
 * que una acción anterior a la respuesta del banner puede acabar contada después de aceptarla. Es el
 * único camino por el que eso pasa, y es propio de Consent Mode.
 *
 * **Y ESTO NO LLEVA LA PUERTA DE `hasStatisticsConsent()`, a diferencia de
 * `gtmPushProfileCompletedEvent`.** No es un descuido, es la diferencia entre los dos emisores. Aquel
 * empuja un evento PROPIO al dataLayer, que fuera del contenedor de GTM no significa nada: lo único
 * que puede hacer algo con él es una etiqueta configurada ahí dentro, con el criterio de consentimiento
 * que tenga puesto, que este repositorio no puede afirmar. Esto manda un evento de GA4 con `send_to`,
 * o sea a través de gtag.js, que es quien implementa Consent Mode: con `analytics_storage` denegado
 * envía medición agregada sin cookies, que es exactamente lo que Consent Mode existe para conservar y
 * lo que este cambio se propuso no perder. Cerrarlo aquí tiraría eso sin ganar nada.
 */
export const safeGtag = (eventName: string, params?: Record<string, unknown>): void => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // El destino se resuelve con `??` y no por orden de propagación: así un llamante puede
    // dirigirlo a otro sitio a propósito (una conversión `AW-`, por ejemplo), pero un `send_to`
    // que llegue como `undefined` no reinstaura en silencio el defecto que este código arregla.
    window.gtag('event', eventName, { ...params, send_to: params?.send_to ?? GA4_MEASUREMENT_ID });
  }
};
